/**
 * IndexedDB 实现：真实后端。
 *
 * 这个文件里没有业务概念，只有"把一个窄接口搬到 IndexedDB 上"以及**失败模式的处理**。
 * 失败模式才是这里的主要工作，因为 IndexedDB 的失败比它看起来要多：
 *
 * | 场景 | 表现 | 这里的处理 |
 * |---|---|---|
 * | 隐私模式 / 禁用站点数据 | `open()` 抛 `SecurityError`，或 `indexedDB` 根本不存在 | 抛 `StorageUnavailableError`，上层降级到内存并如实告知用户 |
 * | 用户回退了版本 | `open()` 抛 `VersionError` | 抛 `StorageSchemaError('version-mismatch')`，并尽量读出库里自述的版本号，给出可读原因 |
 * | 旧数据迁移失败 | 升级事务里的错误 | 中止事务（库回滚到旧版本，**一条数据都没动**）并把原因报出来；绝不删库 |
 * | 配额满 | `put` 抛 `QuotaExceededError` | 原样上抛形状不变，由上层归类成中文提示（见 errors.ts） |
 * | 另一个标签页开着旧版本 | `open()` 收到 `onblocked`，会**一直等** | 超时后拒绝并告诉用户关掉其它标签页，否则界面会永远停在加载中 |
 * | 另一个标签页要升级 | 本连接收到 `versionchange` | 主动关闭连接（不挡别人）并让后续写入抛"需要刷新页面" |
 *
 * 另外两条实现纪律见 `batch()` 与 `keys()` 上的注释。
 *
 * 一个类型上的注意点：这里**不写 `x instanceof IDBDatabase`**。注入进来的可能是
 * 别的实现（测试用 `fake-indexeddb`，Worker 里可能是 polyfill），DOM 全局构造器不一定存在，
 * 也不一定认识对方的实例。需要判断"这是连接还是错误"时，用 `instanceof Error` 反向判断。
 */

import {
  PersistenceError,
  StorageSchemaError,
  StorageUnavailableError,
  isVersionError,
} from './errors';
import {
  MIGRATION_ISSUES_KEY,
  PERSISTENCE_SCHEMA_VERSION,
  SCHEMA_META_KEY,
} from './keys';
import {
  PERSISTENCE_MIGRATIONS,
  migrationIssueReport,
  readMigrationIssueReport,
  readSchemaMeta,
  schemaMeta,
  type MigrationIssue,
  type MigrationStep,
  type SchemaMeta,
} from './schema';
import { assertStorable } from './serialization';
import {
  PERSISTENCE_COLLECTIONS,
  selectKeys,
  type KeyValueStore,
  type PersistenceCollection,
  type StoreKind,
  type StoreOperation,
} from './store';

/** 默认等多久算"被别的标签页挡住了"。用户看得见的等待上限，超过就该给提示而不是继续转圈 */
const DEFAULT_BLOCKED_TIMEOUT_MS = 5000;

export interface IndexedDbStoreOptions {
  /** 数据库名，例如 `hgc` */
  readonly name: string;
  /**
   * `IDBFactory`。显式传入（而不是读全局）有两个好处：测试能把 `fake-indexeddb` 注进来
   * 从而**真正执行**这段实现，生产代码也能在 Worker 里用注入的实例。
   * 传 `null` 表示"这个环境没有/不许用 IndexedDB"。
   */
  readonly factory: IDBFactory | null | undefined;
  /** 目标版本；默认取当前 schema 版本。测试用它打开旧版本数据库以验证迁移 */
  readonly version?: number;
  /** 覆盖迁移链，仅测试用 */
  readonly migrations?: readonly MigrationStep[];
  readonly now?: () => number;
  /** 写进自述记录的写入方标识，例如 `HGC 0.3.0` */
  readonly writtenBy?: string;
  readonly blockedTimeoutMs?: number;
  /** 因为别的标签页要升级而被迫关闭连接时的回调（界面据此提示刷新） */
  readonly onVersionChange?: () => void;
}

interface OpenOutcome {
  readonly db: IDBDatabase;
  readonly issues: readonly MigrationIssue[];
  readonly previousVersion: number;
}

export class IndexedDbStore implements KeyValueStore {
  readonly kind: StoreKind = 'indexeddb';

  /** 打开时库里的版本；0 表示这次是新建 */
  readonly previousVersion: number;
  /** 目标版本（当前 schema 版本） */
  readonly schemaVersion: number;

  private readonly db: IDBDatabase;
  private readonly onVersionChange: (() => void) | undefined;
  private issues: MigrationIssue[];
  private closed = false;
  private closedReason: string | null = null;

  private constructor(
    db: IDBDatabase,
    schemaVersion: number,
    previousVersion: number,
    issues: readonly MigrationIssue[],
    onVersionChange: (() => void) | undefined,
  ) {
    this.db = db;
    this.schemaVersion = schemaVersion;
    this.previousVersion = previousVersion;
    this.issues = [...issues];
    this.onVersionChange = onVersionChange;

    // 另一个标签页要升级数据库时，主动让路。挡着不放的结果是两个标签页都卡住：
    // 对方收到 onblocked 永远等下去，而本标签页的写入也会因为版本冲突随机失败
    db.onversionchange = () => {
      this.closed = true;
      this.closedReason = '另一个标签页正在升级本地数据库';
      db.close();
      this.onVersionChange?.();
    };
    db.onclose = () => {
      this.closed = true;
      this.closedReason ??= '浏览器异常关闭了本地数据库连接（站点存储可能被清除）';
    };
  }

  /**
   * 打开过程中"改不了但已原样保留"的旧记录。
   * 这些记录**仍然在库里**，所以调用方必须能看到它们，而不是以为一切正常。
   */
  get migrationIssues(): readonly MigrationIssue[] {
    return this.issues;
  }

  static async open(options: IndexedDbStoreOptions): Promise<IndexedDbStore> {
    const factory = options.factory;
    if (factory === null || factory === undefined) {
      throw new StorageUnavailableError(
        '当前环境没有 IndexedDB（可能不是浏览器环境，或页面运行在没有存储权限的上下文中）',
      );
    }
    const version = options.version ?? PERSISTENCE_SCHEMA_VERSION;
    const now = options.now ?? Date.now;
    const migrations = options.migrations ?? PERSISTENCE_MIGRATIONS;

    const outcome = await openDatabase({
      factory,
      name: options.name,
      version,
      migrations,
      now,
      blockedTimeoutMs: options.blockedTimeoutMs ?? DEFAULT_BLOCKED_TIMEOUT_MS,
    });

    // 迁移忘了建集合属于开发期缺陷，但表现出来是"用户那里某个功能坏了"。
    // 在这里一次性钉住，比等到第一次写入时抛 NotFoundError 好定位得多
    const missing = PERSISTENCE_COLLECTIONS.filter((name) => !outcome.db.objectStoreNames.contains(name));
    if (missing.length > 0) {
      outcome.db.close();
      throw new StorageSchemaError(
        'migration-failed',
        `数据库缺少集合 ${missing.join('、')}：迁移步骤没有把它们建立起来（程序缺陷）`,
        '请升级到修复后的版本。不要清除站点数据，那会丢掉已存档的参数快照与审计日志。',
      );
    }

    const store = new IndexedDbStore(
      outcome.db,
      version,
      outcome.previousVersion,
      outcome.issues,
      options.onVersionChange,
    );
    await store.writeSchemaMeta(options.writtenBy, now);
    await store.syncMigrationIssues(now, outcome.issues);
    return store;
  }

  async get(collection: PersistenceCollection, key: string): Promise<unknown | undefined> {
    this.assertOpen();
    const tx = this.db.transaction(collection, 'readonly');
    const request = tx.objectStore(collection).get(key);
    const value: unknown = await awaitRequest(request);
    return value === undefined ? undefined : value;
  }

  async put(collection: PersistenceCollection, key: string, value: unknown): Promise<void> {
    assertStorable(value, `${collection}/${key}`);
    await this.batch([{ kind: 'put', collection, key, value }]);
  }

  async remove(collection: PersistenceCollection, key: string): Promise<void> {
    await this.batch([{ kind: 'delete', collection, key }]);
  }

  /**
   * 列出键（字典序）。
   *
   * 刻意**不用 `IDBKeyRange` 做前缀范围查询**：字符串的区间上界要自己拼出"比前缀大的最小字符串"
   * （通常是加一个 `\uffff`），细节取决于实现；拼错的后果是"少了一批记录"或"多读了一批"，
   * 而且只在特定键上出错。这里的量级是千级键，`getAllKeys` 取回后按前缀过滤更简单也更可靠。
   */
  async keys(collection: PersistenceCollection, prefix?: string): Promise<readonly string[]> {
    this.assertOpen();
    const tx = this.db.transaction(collection, 'readonly');
    const request = tx.objectStore(collection).getAllKeys();
    const raw: unknown[] = await awaitRequest(request);
    const names: string[] = [];
    for (const key of raw) {
      if (typeof key === 'string') names.push(key);
    }
    return selectKeys(names, prefix);
  }

  /**
   * 原子批量写。
   *
   * 先把整批值校验完再发请求：一个值存不住时应该在**发出任何写入之前**失败，
   * 这样报错信息能指出是哪个集合的哪个键，而不是一个笼统的 `DataCloneError`。
   * 之后所有请求放在同一个 `readwrite` 事务里，任一条失败 → 事务自动中止 → 整批不生效。
   * 参数快照（头部 + 分片）与航线草稿（正文 + 指针）依赖的正是这一点。
   */
  async batch(operations: readonly StoreOperation[]): Promise<void> {
    this.assertOpen();
    if (operations.length === 0) return;

    for (const operation of operations) {
      if (operation.kind !== 'put') continue;
      assertStorable(operation.value, `${operation.collection}/${operation.key}`);
    }

    const collections = [...new Set(operations.map((operation) => operation.collection))];
    const tx = this.db.transaction(collections, 'readwrite');
    const finished = awaitTransaction(tx);
    try {
      for (const operation of operations) {
        const store = tx.objectStore(operation.collection);
        if (operation.kind === 'delete') store.delete(operation.key);
        else store.put(operation.value, operation.key);
      }
    } catch (error) {
      try {
        tx.abort();
      } catch {
        // 事务已经中止（例如浏览器先一步中止了它）：这里再 abort 会抛，忽略即可
      }
      await finished.catch(() => undefined);
      throw error;
    }
    await finished;
  }

  /**
   * 关闭连接。
   *
   * 关闭之后所有写入都会抛 `StorageUnavailableError`，而不是静默失败 ——
   * "以为在保存、其实什么都没写"是这里最不能接受的结局。
   */
  async close(): Promise<void> {
    if (this.closed) return;
    this.closed = true;
    this.closedReason ??= '本地数据库连接已关闭';
    this.db.close();
  }

  private assertOpen(): void {
    if (!this.closed) return;
    throw new StorageUnavailableError(this.closedReason ?? '本地数据库连接已关闭');
  }

  private async writeSchemaMeta(writtenBy: string | undefined, now: () => number): Promise<void> {
    try {
      await this.put('meta', SCHEMA_META_KEY, schemaMeta(now, writtenBy));
    } catch {
      // 自述记录只是诊断信息：写不进去（例如配额已满）不该让整个应用打不开，
      // 因此这里刻意吞掉，而不是像业务数据那样返回失败
    }
  }

  private async syncMigrationIssues(now: () => number, issues: readonly MigrationIssue[]): Promise<void> {
    try {
      if (issues.length > 0) {
        await this.put('meta', MIGRATION_ISSUES_KEY, migrationIssueReport(now, issues));
        return;
      }
      // 迁移只在版本变化时跑一次；跑完后那几条迁移不了的旧记录仍留在库里，
      // 所以"有没有问题记录"要从落盘的清单里读，而不是只信本次有没有报告
      const existing = readMigrationIssueReport(await this.get('meta', MIGRATION_ISSUES_KEY));
      if (existing === null) return;
      const merged = [...this.issues];
      for (const issue of existing.issues) {
        if (merged.some((known) => known.collection === issue.collection && known.key === issue.key)) continue;
        merged.push(issue);
      }
      this.issues = merged;
    } catch {
      // 同上：诊断信息写不进去不影响可用性
    }
  }
}

interface OpenDatabaseOptions {
  readonly factory: IDBFactory;
  readonly name: string;
  readonly version: number;
  readonly migrations: readonly MigrationStep[];
  readonly now: () => number;
  readonly blockedTimeoutMs: number;
}

/** 打开数据库并跑迁移。所有失败路径都在这里被翻译成可读错误。 */
async function openDatabase(options: OpenDatabaseOptions): Promise<OpenOutcome> {
  const { factory, name, version, migrations, now, blockedTimeoutMs } = options;
  const issues: MigrationIssue[] = [];
  let migrationFailure: unknown = null;
  let previousVersion: number | null = null;

  const db = await new Promise<IDBDatabase>((resolve, reject) => {
    let settled = false;
    let blockedTimer: ReturnType<typeof setTimeout> | null = null;
    const request = factory.open(name, version);

    const settle = (result: IDBDatabase | Error): void => {
      if (settled) {
        // 已经在超时里拒绝过，而请求后来还是成功了：这个连接没人接管，必须关掉，
        // 否则它会一直占着数据库，让下一次打开被 blocked 挡住
        if (!(result instanceof Error)) result.close();
        return;
      }
      settled = true;
      if (blockedTimer !== null) clearTimeout(blockedTimer);
      if (result instanceof Error) reject(result);
      else resolve(result);
    };

    request.onupgradeneeded = (event) => {
      previousVersion = event.oldVersion;
      const tx = request.transaction;
      if (tx === null) {
        migrationFailure = new StorageSchemaError(
          'migration-failed',
          '升级事务不可用，无法迁移本地数据',
          '请重试；若持续失败，请先导出数据再清理站点存储。',
        );
        return;
      }
      try {
        runMigrations({
          tx,
          db: request.result,
          from: event.oldVersion,
          to: version,
          migrations,
          now,
          issues,
        });
      } catch (error) {
        // 迁移失败 → 中止升级事务：库保留在旧版本，**一条旧数据都没动**。
        // 不删库、不清表、不"尽力而为"地跳过 —— 那些做法都会让用户的数据真的消失
        migrationFailure = error;
        try {
          tx.abort();
        } catch {
          // 事务可能已经因为内部错误中止
        }
      }
    };

    request.onsuccess = () => {
      const result = request.result;
      if (migrationFailure !== null) {
        // 事务中止后 open 仍可能走到 onsuccess（部分实现如此）：这个连接不要了
        result.close();
        settle(asError(migrationFailure));
        return;
      }
      settle(result);
    };

    request.onerror = () => {
      const error: unknown = request.error ?? new Error(`打开本地数据库 ${name} 失败`);
      if (migrationFailure !== null) {
        settle(asError(migrationFailure));
        return;
      }
      if (isVersionError(error)) {
        void storedVersionOf(factory, name).then((stored) => {
          const detail =
            stored === null
              ? ''
              : `（库里版本 v${stored.version}${
                  stored.meta?.writtenBy === undefined ? '' : `，由 ${stored.meta.writtenBy} 写入`
                }）`;
          settle(
            new StorageSchemaError(
              'version-mismatch',
              `本地数据库 ${name} 里的数据比当前程序更新${detail}：当前程序只支持到 v${version}，拒绝用旧代码解读更新格式的数据。`,
              '请升级到较新的 HGC 版本后再打开；不要清除站点数据，那会把已存档的参数快照与审计日志一起删掉。',
            ),
          );
        });
        return;
      }
      settle(asError(error));
    };

    request.onblocked = () => {
      if (blockedTimeoutMs <= 0) {
        settle(blockedError(name));
        return;
      }
      // 挡住我们的是另一个还开着旧连接的标签页，而它会一直等下去。必须给一个上限，
      // 否则用户看到的是"打开应用卡住"，而不是"关掉其它标签页"
      blockedTimer = setTimeout(() => settle(blockedError(name)), blockedTimeoutMs);
    };
  });

  return { db, issues, previousVersion: previousVersion ?? version };
}

function blockedError(name: string): PersistenceError {
  return new PersistenceError(
    'blocked',
    `本地数据库 ${name} 正被另一个 HGC 标签页占用，无法完成版本升级。`,
    '请关闭其它 HGC 标签页（或窗口）后重试；当前的改动不会被保存。',
  );
}

function asError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

interface RunMigrationsOptions {
  readonly tx: IDBTransaction;
  readonly db: IDBDatabase;
  readonly from: number;
  readonly to: number;
  readonly migrations: readonly MigrationStep[];
  readonly now: () => number;
  readonly issues: MigrationIssue[];
}

/**
 * 按顺序跑迁移。`from === 0`（新建数据库）也会跑一遍建表步骤 ——
 * 这样"新装用户"与"升级用户"最终得到同一套集合，不会出现只在升级路径上存在的表。
 */
function runMigrations(options: RunMigrationsOptions): void {
  const { tx, db, from, to, migrations, now, issues } = options;
  const context = {
    db,
    tx,
    now,
    report: (issue: MigrationIssue): void => {
      issues.push(issue);
    },
  };

  let current = from;
  while (current < to) {
    const next = current + 1;
    const step = migrations.find((candidate) => candidate.to === next);
    if (step === undefined) {
      // 迁移链有缺口：必须当场炸出来，否则用户库里的数据会停在一个没人认识的中间状态
      throw new StorageSchemaError(
        'migration-failed',
        `本地数据迁移链缺少 v${next} 的步骤（当前程序支持到 v${to}），无法安全升级。`,
        '这是程序缺陷，请升级到修复后的版本；数据未被修改。',
      );
    }
    step.upgrade(context);
    current = next;
  }
}

/** 尽力读出库里的版本与自述记录，用于"版本比程序新"的可读提示 */
async function storedVersionOf(
  factory: IDBFactory,
  name: string,
): Promise<{ readonly version: number; readonly meta: SchemaMeta | null } | null> {
  try {
    const db = await new Promise<IDBDatabase>((resolve, reject) => {
      // 不带版本打开：拿到当前版本，不触发任何升级
      const request = factory.open(name);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(asError(request.error ?? new Error('打开失败')));
      request.onblocked = () => reject(new Error('被占用'));
    });
    try {
      const meta = await new Promise<SchemaMeta | null>((resolve) => {
        if (!db.objectStoreNames.contains('meta')) {
          resolve(null);
          return;
        }
        const request = db.transaction('meta', 'readonly').objectStore('meta').get(SCHEMA_META_KEY);
        request.onsuccess = () => resolve(readSchemaMeta(request.result));
        request.onerror = () => resolve(null);
      });
      return { version: db.version, meta };
    } finally {
      db.close();
    }
  } catch {
    return null;
  }
}

function awaitRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB 请求失败'));
  });
}

function awaitTransaction(tx: IDBTransaction): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB 事务失败'));
    tx.onabort = () =>
      reject(tx.error ?? new PersistenceError('unknown', 'IndexedDB 事务被中止', '请重试。'));
  });
}
